import React, { useCallback, useEffect, useRef, useState } from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import i18n, { type TFunction } from 'i18next';
import * as styles from './facility-display-screen.styles';
import { type StatusState, type Theme } from './facility-display-screen.styles';
import {
  getFacilityDisplayViewConfig,
  getFacilityDisplayViewData,
  pollPairing,
  startPairing,
  type FacilityDisplayViewConfig,
  type FacilityDisplayViewData,
  type FacilityReservationView,
} from './screen.service';
import RouteConstants from '../../../core/routes.constants';

type ScheduleView = 'day' | 'week' | 'month';

const PAIRING_POLL_INTERVAL_MS = 3000;
const DATA_POLL_INTERVAL_MS = 30000;
const CLOCK_TICK_MS = 1000;

// Mockup calendar geometry: 60px per hour row = 1px per minute, full 00:00-24:00 day.
// The visible window opens at 07:00 via the scroll container's initial offset (the
// full day still scrolls), keeping the 00:00-24:00 range the app itself uses.
const HOURS_IN_DAY = 24;
const PX_PER_MINUTE = 1;
const HOUR_HEIGHT = 60;
const INITIAL_VISIBLE_HOUR = 7;

const getDisplayIdFromPath = (): string | undefined => {
  const segments = window.location.pathname.replace(/^\/+|\/+$/g, '').split('/');
  return segments.length > 1 && segments[0] === RouteConstants.PATH_FACILITY_DISPLAY_SCREEN && segments[1]
    ? segments[1]
    : undefined;
};

const displayId = getDisplayIdFromPath();
const TOKEN_STORAGE_KEY = displayId ? `waw_facility_display_token_${displayId}` : 'waw_facility_display_token';
const THEME_STORAGE_KEY = displayId ? `waw_facility_display_theme_${displayId}` : 'waw_facility_display_theme';

// The screen's language comes from the link itself (?lang=), never from the shared
// `whyatwork_language` localStorage key the authenticated portal writes — an admin toggling
// language in another tab of the same browser profile must not change what an unattended kiosk
// (or an external viewer following a shared link) sees, including across reloads. Anything absent
// or unrecognised falls back to NL, so bare links and already-paired kiosks keep their behaviour.
// Keep this whitelist in sync with the Copy-link language options in the Displays section.
const SUPPORTED_SCREEN_LANGUAGES = ['nl', 'en'];
const DEFAULT_SCREEN_LANGUAGE = 'nl';
const LANGUAGE_REASSERT_MS = 5000;

const resolveScreenLanguage = (): string => {
  const requested = new URLSearchParams(window.location.search).get('lang')?.toLowerCase();
  return requested && SUPPORTED_SCREEN_LANGUAGES.includes(requested) ? requested : DEFAULT_SCREEN_LANGUAGE;
};

const SCREEN_LANGUAGE = resolveScreenLanguage();

const localDaysSinceEpoch = (date: Date): number => {
  const utcMidnightOfLocalDate = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  return Math.floor(utcMidnightOfLocalDate / 86400000);
};

const minutesOfDay = (time: string): number => {
  const [hours, minutes] = time.split(':').map((part) => Number(part));
  return (hours || 0) * 60 + (minutes || 0);
};

// Backend serializes TimeOnly as e.g. "19:00:00[]" (pre-existing converter quirk unrelated to
// this feature). Re-derive a clean "HH:mm" for display instead of using the raw string.
const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(':');
  return `${(hours ?? '00').padStart(2, '0')}:${(minutes ?? '00').padStart(2, '0')}`;
};

const fullName = (reservation: FacilityReservationView): string => {
  const first = reservation.user?.firstName ?? '';
  const last = reservation.user?.lastName ?? '';
  return `${first} ${last}`.trim() || reservation.user?.officeName || '';
};

const initialsOf = (reservation: FacilityReservationView): string => {
  const first = reservation.user?.firstName ?? '';
  const last = reservation.user?.lastName ?? '';
  const letters = `${first.charAt(0)}${last.charAt(0)}`.trim();
  return letters || (reservation.user?.officeName ?? '?').charAt(0).toUpperCase();
};

const stateOf = (status: FacilityDisplayViewData['computedStatus']): StatusState => {
  if (status === 'Busy' || status === 1) {
    return 'busy';
  }
  if (status === 'Soon' || status === 2) {
    return 'soon';
  }
  return 'free';
};

// Mirrors the mockup's computeStatus() sub-line copy, derived client-side from the backend's
// now/next reservations plus the live wall-clock.
const statusSubText = (state: StatusState, data: FacilityDisplayViewData | undefined, nowClock: Date, t: TFunction): string => {
  const nowMinutes = nowClock.getHours() * 60 + nowClock.getMinutes();
  if (state === 'busy' && data?.now) {
    const remaining = Math.max(0, minutesOfDay(data.now.endTime) - nowMinutes);
    return t('facility-display-screen-busy-until', { minutes: remaining, time: formatTime(data.now.endTime) });
  }
  if (data?.next) {
    const mins = Math.max(0, minutesOfDay(data.next.startTime) - nowMinutes);
    return mins >= 60
      ? t('facility-display-screen-available-for-hours', { hours: Math.floor(mins / 60), minutes: mins % 60 })
      : t('facility-display-screen-available-for-minutes', { minutes: mins });
  }
  return t('facility-display-screen-no-more-bookings');
};

interface BookCardProps {
  readonly theme: Theme;
  readonly reservation?: FacilityReservationView;
  readonly t: TFunction;
}

const BookCard = ({ theme, reservation, t }: BookCardProps): React.JSX.Element => {
  if (!reservation) {
    return <div className={style(styles.bookEmpty(theme))}>{t('facility-display-screen-nothing-scheduled')}</div>;
  }
  return (
    <div className={style(styles.book(theme))}>
      <div className={style(styles.avatar(theme))}>{initialsOf(reservation)}</div>
      <div>
        <div className={style(styles.bookWho)}>{fullName(reservation)}</div>
        <div className={style(styles.bookWhen(theme))}>
          {formatTime(reservation.startTime)} — {formatTime(reservation.endTime)}
          {reservation.title ? ` · ${reservation.title}` : ''}
        </div>
        {reservation.user?.officeName && <div className={style(styles.bookOrg(theme))}>{reservation.user.officeName}</div>}
      </div>
    </div>
  );
};

const useScrollToInitialHour = (): React.RefObject<HTMLDivElement> => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = INITIAL_VISIBLE_HOUR * HOUR_HEIGHT;
    }
  }, []);
  return ref;
};

/// Identifies the single reservation instance currently in progress, so the grids can colour it
/// differently. Only set while the facility's computed status is Busy.
///
/// `day` is TODAY, deliberately — not `data.now.daysSinceEpoch`. The backend maps `Now` straight
/// from the entity without rewriting DaysSinceEpoch (unlike the expanded `reservations` list, where
/// each occurrence is stamped with the day it falls on), so for a repeating booking `now`
/// carries the day of the FIRST occurrence. Matching on id alone would then light up every
/// occurrence of that series across the week and month grids.
interface ActiveReservation {
  readonly id: string;
  readonly day: number;
}

const isActiveReservation = (reservation: FacilityReservationView, active: ActiveReservation | undefined): boolean =>
  active !== undefined && reservation.id === active.id && reservation.daysSinceEpoch === active.day;

interface DayEventsProps {
  readonly theme: Theme;
  readonly reservations: FacilityReservationView[];
  readonly withDetails: boolean;
  readonly active?: ActiveReservation;
}

const DayEvents = ({ theme, reservations, withDetails, active }: DayEventsProps): React.JSX.Element => (
  <>
    {reservations.map((reservation) => {
      const top = minutesOfDay(reservation.startTime) * PX_PER_MINUTE;
      const height = Math.max((minutesOfDay(reservation.endTime) - minutesOfDay(reservation.startTime)) * PX_PER_MINUTE - 2, 16);
      return (
        <div key={reservation.id} className={style(styles.ev(theme, isActiveReservation(reservation, active)))} style={{ top, height }}>
          {withDetails ? (
            <>
              <div className={style(styles.evWho)}>
                <span className={style(styles.evMini(theme))}>{initialsOf(reservation)}</span> {fullName(reservation)}
              </div>
              <div className={style(styles.evTitle)}>{reservation.title || fullName(reservation)}</div>
              <div className={style(styles.evSub)}>
                {formatTime(reservation.startTime)}–{formatTime(reservation.endTime)}
                {reservation.user?.officeName ? ` · ${reservation.user.officeName}` : ''}
              </div>
            </>
          ) : (
            <>
              <div className={style(styles.evTitle)}>
                {formatTime(reservation.startTime)} {reservation.title || fullName(reservation)}
              </div>
              <div className={style(styles.evSub)}>{fullName(reservation)}</div>
            </>
          )}
        </div>
      );
    })}
  </>
);

interface HourScaleProps {
  readonly theme: Theme;
  /// Week view passes false so the grid carries no horizontal rules — the only one left is the
  /// weekday header's. Day view leaves it on, where the hour lines are the main time reference.
  readonly withLines?: boolean;
}

const HourColumn = ({ theme, withLines = true }: HourScaleProps): React.JSX.Element => (
  <div className={style(styles.hours)}>
    {Array.from({ length: HOURS_IN_DAY }, (_, hour) => (
      <div key={hour} className={style(styles.hour(theme, withLines))}>
        {`${hour.toString().padStart(2, '0')}:00`}
      </div>
    ))}
  </div>
);

const HourRows = ({ theme, withLines = true }: HourScaleProps): React.JSX.Element => (
  <>
    {Array.from({ length: HOURS_IN_DAY }, (_, hour) => (
      <div key={hour} className={style(styles.gridRow(theme, withLines))} />
    ))}
  </>
);

interface ScheduleProps {
  readonly theme: Theme;
  readonly reservations: FacilityReservationView[];
  readonly now: Date;
  readonly active?: ActiveReservation;
}

const DayView = ({ theme, reservations, now, active }: ScheduleProps): React.JSX.Element => {
  const scrollRef = useScrollToInitialHour();
  const today = localDaysSinceEpoch(now);
  const todays = reservations.filter((reservation) => reservation.daysSinceEpoch === today);
  const nowTop = (now.getHours() * 60 + now.getMinutes()) * PX_PER_MINUTE;

  return (
    <div ref={scrollRef} className={style(styles.dayScroll)}>
      <div className={style(styles.day)}>
        <HourColumn theme={theme} />
        <div className={style(styles.grid(theme))}>
          <HourRows theme={theme} />
          <DayEvents theme={theme} reservations={todays} withDetails active={active} />
          <div className={style(styles.nowLine(theme))} style={{ top: nowTop }}>
            <span className={style(styles.nowLineDot(theme))} />
          </div>
        </div>
      </div>
    </div>
  );
};

const WeekView = ({ theme, reservations, now, active }: ScheduleProps): React.JSX.Element => {
  const scrollRef = useScrollToInitialHour();
  const today = localDaysSinceEpoch(now);
  const dayOfWeek = now.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  const weekStart = today + mondayOffset;
  const days = Array.from({ length: 7 }, (_, index) => weekStart + index);
  const totalHeight = HOURS_IN_DAY * HOUR_HEIGHT;

  return (
    <div ref={scrollRef} className={style(styles.weekScroll)}>
      <div className={style(styles.weekHeadGrid)}>
        <div />
        {days.map((day) => {
          const date = new Date(day * 86400000);
          const isToday = day === today;
          return (
            <div key={day} className={style(styles.colHead(theme, isToday))}>
              <div>{date.toLocaleDateString(SCREEN_LANGUAGE, { weekday: 'short', timeZone: 'UTC' })}</div>
              <div className={style(styles.colHeadNum(theme, isToday))}>{date.getUTCDate()}</div>
            </div>
          );
        })}
      </div>
      <div className={style(styles.weekBodyGrid)} style={{ minHeight: totalHeight }}>
        <HourColumn theme={theme} withLines={false} />
        {days.map((day) => {
          const dayReservations = reservations.filter((reservation) => reservation.daysSinceEpoch === day);
          return (
            <div key={day} className={style(styles.dayCol(theme))}>
              <HourRows theme={theme} withLines={false} />
              <DayEvents theme={theme} reservations={dayReservations} withDetails={false} active={active} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MonthView = ({ theme, reservations, now, active }: ScheduleProps): React.JSX.Element => {
  const maxPills = 4;
  const today = localDaysSinceEpoch(now);
  const firstOfMonth = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
  const startOffset = firstOfMonth.getUTCDay() === 0 ? 6 : firstOfMonth.getUTCDay() - 1;
  const firstCellDay = localDaysSinceEpoch(firstOfMonth) - startOffset;
  const cells = Array.from({ length: 42 }, (_, index) => firstCellDay + index);
  // Derived from the screen's own language rather than hardcoded, so an EN link reads Mon/Tue…
  // and an NL link reads ma/di…. Built off a known Monday so the week always starts on Monday.
  const weekdayLabels = Array.from({ length: 7 }, (_, index) =>
    new Date(Date.UTC(2024, 0, 1 + index)).toLocaleDateString(SCREEN_LANGUAGE, { weekday: 'short', timeZone: 'UTC' }));

  return (
    <>
      <div className={style(styles.monthHeadRow)}>
        {weekdayLabels.map((label) => (
          <div key={label} className={style(styles.monthHeadCell(theme))}>
            {label}
          </div>
        ))}
      </div>
      <div className={style(styles.month)}>
        {cells.map((day) => {
          const date = new Date(day * 86400000);
          const isOther = date.getUTCMonth() !== now.getMonth();
          const isToday = day === today;
          const dayReservations = reservations.filter((reservation) => reservation.daysSinceEpoch === day).slice(0, maxPills);
          return (
            <div key={day} className={style(styles.monthCell(theme, isOther))}>
              <span className={style(styles.monthNum(theme, isToday))}>{date.getUTCDate()}</span>
              {dayReservations.map((reservation) => (
                <div key={reservation.id} className={style(styles.monthPill(theme, isActiveReservation(reservation, active)))}>
                  {formatTime(reservation.startTime)} {reservation.title || fullName(reservation)}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </>
  );
};

type ScreenState =
  | { phase: 'pairing'; code?: string; pollToken?: string }
  | { phase: 'live'; config: FacilityDisplayViewConfig; data?: FacilityDisplayViewData }
  | { phase: 'paused'; config: FacilityDisplayViewConfig }
  | { phase: 'loading' }
  | { phase: 'error'; message: string };

const FacilityDisplayScreen = (): React.JSX.Element => {
  const { t } = useTranslation();
  const [state, setState] = useState<ScreenState>({ phase: 'loading' });
  const [now, setNow] = useState(new Date());
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | undefined>(undefined);
  const [scheduleView, setScheduleView] = useState<ScheduleView>('day');
  const [theme, setTheme] = useState<Theme>(() => ((localStorage.getItem(THEME_STORAGE_KEY) as Theme | null) === 'eink' ? 'eink' : 'dark'));

  useEffect(() => {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), CLOCK_TICK_MS);
    return (): void => clearInterval(interval);
  }, []);

  const clearToken = useCallback((): void => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
  }, []);

  const beginPairing = useCallback(async (): Promise<void> => {
    const session = await startPairing();
    if (!session) {
      setState({ phase: 'error', message: t('facility-display-screen-error-unreachable') });
      setTimeout(beginPairing, PAIRING_POLL_INTERVAL_MS);
      return;
    }
    setState({ phase: 'pairing', code: session.pairingCode, pollToken: session.pollToken });
  }, [t]);

  // Pin the link's language and keep re-asserting it: the kiosk shares i18next (and its
  // localStorage key) with the authenticated portal, so an admin switching language elsewhere in
  // the same browser would otherwise drift this screen mid-session or on its next self-reload.
  useEffect(() => {
    const pin = (): void => {
      if (i18n.language !== SCREEN_LANGUAGE) {
        i18n.changeLanguage(SCREEN_LANGUAGE);
      }
    };
    pin();
    const interval = setInterval(pin, LANGUAGE_REASSERT_MS);
    window.addEventListener('focus', pin);
    document.addEventListener('visibilitychange', pin);
    return (): void => {
      clearInterval(interval);
      window.removeEventListener('focus', pin);
      document.removeEventListener('visibilitychange', pin);
    };
  }, []);

  useEffect(() => {
    if (state.phase !== 'pairing' || !state.pollToken) {
      return undefined;
    }

    const { pollToken } = state;
    const interval = setInterval(async () => {
      const result = await pollPairing(pollToken);
      if (!result) {
        return;
      }
      if (result.status === 'paired' && result.displayToken) {
        localStorage.setItem(TOKEN_STORAGE_KEY, result.displayToken);
        setState({ phase: 'loading' });
      } else if (result.status === 'expired') {
        clearInterval(interval);
        beginPairing();
      }
    }, PAIRING_POLL_INTERVAL_MS);

    return (): void => clearInterval(interval);
  }, [state, beginPairing]);

  // A visitor who opened a shared link before an admin switched sharing on lands on a pairing
  // code, which is useless to them. Re-attempt the anonymous read on the same cadence as the live
  // poll so the screen heals itself the moment sharing is enabled, instead of sitting on an
  // unusable code until someone reloads it by hand.
  useEffect(() => {
    if (state.phase !== 'pairing' || !displayId || localStorage.getItem(TOKEN_STORAGE_KEY)) {
      return undefined;
    }

    const interval = setInterval(async () => {
      const result = await getFacilityDisplayViewConfig(undefined, displayId);
      if (result.status !== 401 && result.config) {
        setState({ phase: 'loading' });
      }
    }, DATA_POLL_INTERVAL_MS);

    return (): void => clearInterval(interval);
  }, [state.phase]);

  const rangeForView = useCallback((view: ScheduleView): { start: number; end: number } => {
    const today = localDaysSinceEpoch(new Date());
    if (view === 'day') {
      return { start: today, end: today };
    }
    if (view === 'week') {
      const dayOfWeek = new Date().getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const start = today + mondayOffset;
      return { start, end: start + 6 };
    }
    return { start: today - 7, end: today + 35 };
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) ?? undefined;
    // Without a paired token, try reading the display anonymously by id — that succeeds only if
    // an admin opted this display into public sharing. Public viewers are never issued or stored
    // a token, so forwarding the link can't hand anyone a credential.
    const publicDisplayId = token ? undefined : displayId;
    if (!token && !publicDisplayId) {
      beginPairing();
      return;
    }

    const configResult = await getFacilityDisplayViewConfig(token, publicDisplayId);
    if (configResult.status === 401) {
      // Either the paired token was revoked, or this display isn't shared publicly. Both land on
      // the pairing handshake; a link that's simply not shared yet self-heals once it is.
      if (token) {
        clearToken();
      }
      beginPairing();
      return;
    }
    if (!configResult.config) {
      setState({ phase: 'error', message: t('facility-display-screen-error-config') });
      setTimeout(refresh, DATA_POLL_INTERVAL_MS);
      return;
    }

    if (configResult.config.status === 'paused') {
      setState({ phase: 'paused', config: configResult.config });
      return;
    }

    const { start, end } = rangeForView(scheduleView);
    const dataResult = await getFacilityDisplayViewData(token, start, end, publicDisplayId);
    if (dataResult.status === 401) {
      if (token) {
        clearToken();
      }
      beginPairing();
      return;
    }

    // A failed data call (500, a network blip returning status -1, a 423 because the display was
    // paused between the config and data calls) must not wipe the screen: every block below is
    // guarded on `data`, so committing `data: undefined` blanks an unattended kiosk for a whole poll
    // interval. Keep what is already on screen, and don't claim a refresh that didn't happen.
    const { config } = configResult;
    setState((previous) => ({
      phase: 'live',
      config,
      data: dataResult.data ?? (previous.phase === 'live' ? previous.data : undefined),
    }));

    if (dataResult.data) {
      setLastRefreshedAt(new Date());
    }
  }, [beginPairing, clearToken, rangeForView, scheduleView, t]);

  useEffect(() => {
    if (state.phase === 'loading') {
      refresh();
    }
  }, [state.phase, refresh]);

  useEffect(() => {
    if (state.phase !== 'live' && state.phase !== 'paused') {
      return undefined;
    }
    const interval = setInterval(refresh, DATA_POLL_INTERVAL_MS);
    return (): void => clearInterval(interval);
  }, [state.phase, refresh]);

  // Re-fetch immediately when the user switches Day/Week/Month instead of waiting for the next poll tick.
  useEffect(() => {
    if (state.phase === 'live' || state.phase === 'paused') {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleView]);

  const secondsSinceRefresh = lastRefreshedAt ? Math.max(0, Math.floor((now.getTime() - lastRefreshedAt.getTime()) / 1000)) : 0;

  const toggleTheme = (): void => setTheme(theme === 'dark' ? 'eink' : 'dark');

  if (state.phase === 'pairing') {
    return (
      <div className={style(styles.frame(theme))}>
        <button type="button" className={style(styles.themeToggle(theme))} onClick={toggleTheme}>
          {t('facility-display-screen-theme-toggle')}
        </button>
        <div className={style(styles.centeredMessage)}>
          <h1>{t('facility-display-screen-pair-title')}</h1>
          <p>{t('facility-display-screen-pair-instructions')}</p>
          <div className={style(styles.pairingCode)}>{state.code ?? '------'}</div>
        </div>
      </div>
    );
  }

  if (state.phase === 'loading') {
    return (
      <div className={style(styles.frame(theme))}>
        <div className={style(styles.centeredMessage)}>{t('facility-display-screen-loading')}</div>
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div className={style(styles.frame(theme))}>
        <div className={style(styles.centeredMessage)}>{state.message}</div>
      </div>
    );
  }

  const { config } = state;
  const data = state.phase === 'live' ? state.data : undefined;
  const state0: StatusState = data ? stateOf(data.computedStatus) : 'free';
  const statusLabelText = state0 === 'busy' ? t('facility-display-screen-in-use') : t('facility-display-screen-available');
  const statusBigText = state0 === 'busy' ? t('facility-display-screen-busy') : t('facility-display-screen-free');
  const subText = data ? statusSubText(state0, data, now, t) : t('facility-display-screen-no-more-bookings');

  // Only while the facility is actually Busy — a 'soon' status also carries a reservation
  // (in `data.next`), but nothing is in progress yet, so nothing should be highlighted.
  const activeReservation: ActiveReservation | undefined =
    state0 === 'busy' && data?.now ? { id: data.now.id, day: localDaysSinceEpoch(now) } : undefined;

  const dateLabel = now.toLocaleDateString(SCREEN_LANGUAGE, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const clockLabel = now.toTimeString().slice(0, 5);

  const scheduleTitle = (): string => {
    if (scheduleView === 'day') {
      return t('facility-display-screen-today-prefix', {
        date: now.toLocaleDateString(SCREEN_LANGUAGE, { weekday: 'long', month: 'long', day: 'numeric' }),
      });
    }
    if (scheduleView === 'week') {
      return t('facility-display-screen-this-week');
    }
    return now.toLocaleDateString(SCREEN_LANGUAGE, { month: 'long', year: 'numeric' });
  };
  const schedTitle = scheduleTitle();

  const reservations = data?.reservations ?? [];

  return (
    <div className={style(styles.frame(theme))}>
      <button type="button" className={style(styles.themeToggle(theme))} onClick={toggleTheme}>
        {t('facility-display-screen-toggle-eink')}
      </button>

      <div className={style(styles.head)}>
        <div className={style(styles.room)}>
          <span className={style(styles.calIcon(theme))}>
            <span className={style(styles.calIconMonth(theme))}>
              {now.toLocaleDateString(SCREEN_LANGUAGE, { month: 'short' }).toUpperCase()}
            </span>
            <span className={style(styles.calIconDay(theme))}>{now.getDate()}</span>
          </span>
          {config.facilityName ?? config.name}
        </div>
        <div className={style(styles.bookCta(theme))}>
          <div className={style(styles.ctaLabel)}>
            <div className={style(styles.ctaT)}>{t('facility-display-screen-login-to-book')}</div>
          </div>
        </div>
        <div className={style(styles.clockWrap)}>
          <div className={style(styles.clock)}>{clockLabel}</div>
          <div className={style(styles.subdate(theme))}>{dateLabel}</div>
          <div className={style(styles.refresh(theme))}>
            <span className={style(styles.dot(theme))} />
            <span>
              {state.phase === 'paused'
                ? t('facility-display-screen-paused')
                : t('facility-display-screen-live-refreshed', { seconds: secondsSinceRefresh })}
            </span>
          </div>
        </div>
      </div>

      <div className={style(styles.now)}>
        <div className={style(styles.statusCard(theme, state0))}>
          <div className={style(styles.statusLabel(theme))}>
            {state.phase === 'paused' ? t('facility-display-screen-paused-label') : statusLabelText}
          </div>
          <div className={style(styles.statusBig(theme, state0))}>{state.phase === 'paused' ? '—' : statusBigText}</div>
          <div className={style(styles.statusSub(theme))}>
            {state.phase === 'paused' ? t('facility-display-screen-display-paused') : subText}
          </div>
        </div>
        <div className={style(styles.nextCard(theme))}>
          <h3 className={style(styles.nextH3(theme))}>{t('facility-display-screen-now')}</h3>
          <BookCard theme={theme} reservation={data?.now} t={t} />
          <h3 className={style(styles.nextH3(theme))} style={{ marginTop: 4 }}>
            {t('facility-display-screen-up-next')}
          </h3>
          <BookCard theme={theme} reservation={data?.next} t={t} />
        </div>
      </div>

      <div className={style(styles.schedule(theme))}>
        <div className={style(styles.schedHead)}>
          <div className={style(styles.schedTitle)}>{schedTitle}</div>
          <div className={style(styles.viewSwitch(theme))}>
            {(['day', 'week', 'month'] as ScheduleView[]).map((view) => (
              <button
                key={view}
                type="button"
                className={style(styles.viewSwitchButton(theme, scheduleView === view))}
                onClick={(): void => setScheduleView(view)}
              >
                {t(`facility-display-screen-view-${view}`)}
              </button>
            ))}
          </div>
        </div>
        <div className={style(styles.schedBody)}>
          {scheduleView === 'day' && <DayView theme={theme} reservations={reservations} now={now} active={activeReservation} />}
          {scheduleView === 'week' && <WeekView theme={theme} reservations={reservations} now={now} active={activeReservation} />}
          {scheduleView === 'month' && <MonthView theme={theme} reservations={reservations} now={now} active={activeReservation} />}
        </div>
      </div>
    </div>
  );
};

export default FacilityDisplayScreen;
